import React, { useCallback } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import styled from 'styled-components';

import type { IResourceItemType } from '@/api/types';

import { useAppDispatch, useAppSelector, useKeyDown } from '@/hooks';
import {
  selectIsPreviewOpen,
  selectPendingAction,
  selectSelectedResourceDetails,
} from '@/store/selectors/resourcesSelectors';
import { closePreview, openPreview } from '@/store/slices/resourcesSlice';
import { media } from '@/styles/theme';
import { otherColors } from '@/styles/colors';
import { NAVBAR_HEIGHT, Z_INDEX_MODAL } from '@/config/variables';
import { Backdrop, LoaderWrapper } from '@/components/common';
import { ResourcePreviewHeader } from './ResourcePreviewHeader';
import { PreviewBody } from './body';
import { ResourcePreviewSkeleton } from './ResourcePreview.skeleton';

const resourcePreviewSelector = createSelector(
  selectIsPreviewOpen,
  selectSelectedResourceDetails,
  selectPendingAction(openPreview.typePrefix),
  (isOpen, preview, isOpening) => ({
    isOpen,
    preview,
    isOpening,
  }),
);

const ResourcePreview = (): React.ReactElement => {
  const { isOpen, preview, isOpening } = useAppSelector(resourcePreviewSelector);
  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => {
    dispatch(closePreview());
  }, [dispatch]);

  useKeyDown('Escape', handleClose);

  return (
    <Container show={isOpen}>
      <Drawer show={isOpen}>
        <LoaderWrapper isLoading={isOpening} loader={<ResourcePreviewSkeleton />}>
          {preview ? (
            <>
              <ResourcePreviewHeader
                onClose={handleClose}
                postedDate={(preview as IResourceItemType)?.dateStart}
                data={preview}
              />
              <PreviewBody resource={preview} />
            </>
          ) : (
            <></>
          )}
        </LoaderWrapper>
      </Drawer>
      <Backdrop show={isOpen} onClick={handleClose} />
    </Container>
  );
};

export { ResourcePreview };

interface ContainerProps {
  show: boolean;
}

const Drawer = styled.div<ContainerProps>`
  position: fixed;
  height: ${`calc(100vh - ${NAVBAR_HEIGHT}px)`};
  width: 100%;
  background-color: ${otherColors.white};
  z-index: ${Z_INDEX_MODAL};
  padding: 24px 16px;
  overflow: auto;
  right: ${({ show }) => (show ? '0px' : '-100%')};

  transition: right 0.3s ease-in-out;

  ${media.tablet} {
    max-width: 800px;
    min-width: 764px;
  }

  ${media.laptop} {
    right: ${({ show }) => (show ? 'calc((100vw - 1440px) / 2);' : '-100%')};
  }

  ${media.phone} {
    padding: 24px 32px;
  }
`;

const Container = styled.div<ContainerProps>`
  position: absolute;
  width: 100vw;
  z-index: ${Z_INDEX_MODAL};
  ${({ show }) => !show && 'pointer-events: none;'}
`;
